interface RetryableRequestConfig {
  _retry?: boolean;
  headers?: Record<string, string>;
}

interface UnauthorizedError {
  config?: RetryableRequestConfig;
  response?: {
    status?: number;
  };
}

interface HandleUnauthorizedParams<T = unknown> {
  error: UnauthorizedError;
  refreshToken: () => Promise<string>;
  retryRequest: (request: RetryableRequestConfig) => Promise<T>;
  onUnauthorized: () => void;
}

export async function handleUnauthorizedResponse<T>({
  error,
  refreshToken,
  retryRequest,
  onUnauthorized,
}: HandleUnauthorizedParams<T>): Promise<T> {
  const originalRequest = error.config;

  if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
    originalRequest._retry = true;

    // Fase 1: tentar renovar o token
    let newToken: string;
    try {
      newToken = await refreshToken();
    } catch {
      // Refresh falhou → sessão morta
      onUnauthorized();
      (error as Record<string, unknown>)._authHandled = true;
      return Promise.reject(error);
    }

    // Fase 2: retry com o novo token (await para capturar rejeição)
    originalRequest.headers = originalRequest.headers ?? {};
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    try {
      return await retryRequest(originalRequest);
    } catch (retryError) {
      // Retry falhou — se for outro 401, redirecionar para login
      if ((retryError as UnauthorizedError)?.response?.status === 401) {
        onUnauthorized();
        (retryError as Record<string, unknown>)._authHandled = true;
      }
      return Promise.reject(retryError);
    }
  }

  if (error.response?.status === 401) {
    onUnauthorized();
    (error as Record<string, unknown>)._authHandled = true;
  }

  return Promise.reject(error);
}
